import { InstrumentRow } from '@/components/catalogo/types';
import { displayInstrument } from './normalize-ticker';

type SearchableInstrument = Pick<
  InstrumentRow,
  'idCem' | 'name' | 'fundTypeSymbol' | 'companyName' | 'fundTypeDescription'
>;

const normalizeSearchText = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');

const searchableValues = (option: SearchableInstrument) => [
  displayInstrument(option.idCem, option.fundTypeSymbol),
  option.idCem,
  option.name,
  option.companyName ?? '',
  option.fundTypeDescription ?? '',
];

const relevanceScore = (option: SearchableInstrument, query: string) => {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) return 0;

  const [displayedInstrument, canonicalInstrument, name, companyName, fundTypeDescription] =
    searchableValues(option).map(normalizeSearchText);
  const canonicalValues = [displayedInstrument, canonicalInstrument];
  const nameValue = name;
  const metadataValues = [companyName, fundTypeDescription];

  if (canonicalValues.some((value) => value === normalizedQuery)) return 400;
  if (nameValue === normalizedQuery) return 350;
  if (canonicalValues.some((value) => value.startsWith(normalizedQuery))) return 300;
  if (nameValue.startsWith(normalizedQuery)) return 250;
  if (canonicalValues.some((value) => value.includes(normalizedQuery))) return 200;
  if (nameValue.includes(normalizedQuery)) return 150;
  if (metadataValues.some((value) => value.startsWith(normalizedQuery))) return 50;
  if (metadataValues.some((value) => value.includes(normalizedQuery))) return 10;
  return 0;
};

export const tickerMatchesQuery = (option: SearchableInstrument, query: string) => {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) return query.trim().length === 0;

  return searchableValues(option).some((value) => normalizeSearchText(value).includes(normalizedQuery));
};

export const rankInstrumentOptions = <T extends SearchableInstrument>(options: T[], query: string) =>
  options
    .map((option, index) => ({ option, index, score: relevanceScore(option, query) }))
    .sort((left, right) => right.score - left.score || left.index - right.index)
    .map(({ option }) => option);

export const findInstrumentMatch = <T extends SearchableInstrument>(options: T[], query: string) => {
  if (!normalizeSearchText(query)) return undefined;

  return rankInstrumentOptions(
    options.filter((option) => tickerMatchesQuery(option, query)),
    query,
  )[0];
};

export const shouldNavigateInstrumentSearch = (loading: boolean, matchedOption: unknown, highlightedOption: unknown) =>
  !loading && matchedOption != null && highlightedOption == null;
